import React, { useContext } from 'react'
import { RingsSpinner } from '../../../components/Spinner'
import styled from 'styled-components'

import { getTowingRecordActions } from '../../../common/AccessControl'
import { towingRecordServiceTypeOptions } from '../../../common/options'
import { TowingRecordPaymentType, TypeIdentifier } from '../../../common/types'
import { Button } from '../../../components/Button'
import { getStatus } from '../../../components/JobStatus'
import { getDeliverable, getJobTypeDescription } from '../../../components/JobTypeDescription'
import { Text } from '../../../components/layout'
import { ActionButtonGrid } from '../../../components/layout/buttonlayout'
import { useHistoryPush } from '../../../components/router'
import { Styling } from '../../../components/Styling'
import { TopNavigation } from '../../../components/TopNavigation/TopNavigation'
import { SummaryHeader } from '../../../components/TowingCommon/SummaryHeader'
import { getPrepaymentLabel } from '../../../components/TowingCommon/getPrepaymentLabel'
import { trackedSosStatuses, trackedStatuses } from '../../../components/TrackedJobs/types'
import { StateStore } from '../../../containers/StoreProvider'
import { getStationByIdFromSettings } from '../../Parameters/station'
import { getOperatorLabelFromId } from '../../Parameters/user'
import { createRouteMenuConfig, finishRouteAddressRequired } from '../components/routes/createRouteMenuConfig'
import { RoutesMenu } from '../components/routes/RoutesMenu'
import { EditRouteSubforms } from '../components/routes/types'
import { SummaryBody } from '../components/SummaryBody'
import { TowingRecord } from './types'
import { Maybe } from '../../../common/types'
import { toALScalarTimestamp } from '../components/routes/subforms/ArrivalRoute/mutation/toMutationVariables'
import { useGetCurrentTimeDifference } from '../../../util/useGetCurrentTimeDifference'
import { formatMessageSafe } from '../../../util/translateInputOptions'
import { useIntl } from 'react-intl'
import { messages as towingJobsMessages } from '../../TowingJobsPage/messages'
import { messages } from '../messages'
import { SendDocumentForm, toSendDocumentOptions } from './SendDocumentForm'
import { SendActionsDropdown } from './SendActionsDropdown'
import { useSettings } from '../../Parameters'
import { isRouteEditable } from '../../../util/towingRecord/isRouteEditable'
import { SendSmsDialog } from '../../../components/SendSmsDialog/SendSmsDialog'
import { canShowSendSmsOnRecord } from '../../../components/SendSmsDialog/canShowSendSms'

const SOS_GPS_EMIT_DELAY = 60

const Tracking = styled.div`
  margin: 0 auto;
  display: flex;
  align-items: center;
  font-size: 0.9rem;
`

const TrackingLoaderWrapper = styled.span`
  color: ${props => props.theme.colors.grey};
  margin-top: 3px;
  margin-right: 5px;
  display: inline-flex;
`
interface Props {
  towingRecord: TowingRecord
  onEditAction: () => void
  onSelectSubform: (id: EditRouteSubforms, done: boolean) => void
  onRefetch?: () => void
  onVehicleInfoLinkClick: () => void
  lockedFor: Maybe<string>
  refetchData?: () => void
  imagesCount: number
}

const getSlug = (towingRecord: TowingRecord): string => {
  if (isRouteEditable(towingRecord)) {
    return ''
  }

  const completeStatuses = ['payment_undefined', 'aggregate', 'complete', 'billing']

  return towingRecord && towingRecord.status && completeStatuses.includes(towingRecord.status) ? 'completed' : ''
}

function getJobStartedDate(towingRecord: TowingRecord) {
  const result =
    towingRecord.jobStartedAt && towingRecord.jobStartedAt.date instanceof Date ? towingRecord.jobStartedAt : null

  if (!result) return null

  const dateTime = toALScalarTimestamp(result)

  if (!dateTime.date || !dateTime.time) return null

  return new Date(dateTime.date.value + ' ' + dateTime.time.value)
}

const ViewTowingRecordContentIntl: React.FunctionComponent<Props> = ({
  onEditAction,
  towingRecord,
  onSelectSubform,
  onVehicleInfoLinkClick,
  lockedFor,
  refetchData,
  imagesCount
}) => {
  const [showSendForm, setShowSendForm] = React.useState(false)
  const [showSendSmsDialog, setShowSendSmsDialog] = React.useState(false)
  const intl = useIntl()
  const formatMessage = formatMessageSafe(intl)
  const { state } = useContext(StateStore)
  const settings = useSettings()
  const jobStarted = getJobStartedDate(towingRecord)

  const { difference } = useGetCurrentTimeDifference(jobStarted, {
    onUpdate: (value, timerId) => {
      if (value >= SOS_GPS_EMIT_DELAY && timerId) clearInterval(timerId)
    },
  })

  const broadcastGpsCoordinates = difference >= SOS_GPS_EMIT_DELAY

  const useExtendedRoutes = settings ? settings.modules.useExtendedRoutes : false

  const routeMenuConfig = createRouteMenuConfig(
    towingRecord.routes,
    finishRouteAddressRequired(towingRecord.jobDetails.serviceType, towingRecord.type),
    towingRecord.type,
    useExtendedRoutes,
    formatMessage
  )

  const slug: string = getSlug(towingRecord)
  const { useJobButtons, editRoutes } = getTowingRecordActions(state.currentUser, towingRecord)
  const showSendSms = canShowSendSmsOnRecord(state.currentUser, towingRecord, lockedFor)

  const historyPush = useHistoryPush()

  const billingStatus = towingRecord.billing?.[0]?.status
  const normalizedStatus = billingStatus === 'sent' ? 'link_sent' : billingStatus
  const paymentLabel =
    towingRecord.paymentType === TowingRecordPaymentType.online_payment &&
    getPrepaymentLabel(id => formatMessage({ id }), normalizedStatus, {
      fallback: formatMessage(towingJobsMessages.paymentLinkSent),
      prepayment: towingRecord.prepayment,
    })

  return (
    <>
      <TopNavigation
        editAction={onEditAction}
        slug={slug}
        editAddress={`/towing-record/${towingRecord.id}/edit`}
        disableEdit={!!lockedFor}
      />
      <SummaryHeader
        jobTypeDescription={getJobTypeDescription(
          intl,
          towingRecord.jobDetails.serviceType,
          getStationByIdFromSettings(towingRecord.stationId, settings),
          towingRecord.label,
          towingRecord.typeIdentifier,
          getDeliverable(towingRecord.sosServiceOrder)
        )}
        status={getStatus(
          intl,
          towingRecord.status,
          getOperatorLabelFromId(
            towingRecord.operatorId,
            state.currentUser,
            settings ? settings.operators : null,
            id => formatMessage({ id })
          )
        )}
        type={formatMessage({ id: towingRecordServiceTypeOptions.options[towingRecord.jobDetails.serviceType] })}
        station={getStationByIdFromSettings(towingRecord.stationId, settings)}
        vehicleDetails={towingRecord.vehicleDetails}
        towingDate={towingRecord.jobDetails.towingDate}
        paymentLabel={paymentLabel || null}
      />
      {((towingRecord.typeIdentifier === TypeIdentifier.sos && trackedSosStatuses.includes(towingRecord.jobStatus)) ||
        (towingRecord.typeIdentifier === TypeIdentifier.autoliitto &&
          trackedStatuses.includes(towingRecord.jobStatus))) && (
        <>
          {towingRecord.typeIdentifier === TypeIdentifier.autoliitto && (
            <Tracking>
              <TrackingLoaderWrapper><RingsSpinner visible={true} color="currentColor" width={25} height={25} /></TrackingLoaderWrapper>
              <Text>{formatMessage(messages.tracking_autoliitto)}</Text>
            </Tracking>
          )}
          {towingRecord.typeIdentifier === TypeIdentifier.sos && (
            <Tracking>
              <TrackingLoaderWrapper><RingsSpinner visible={true} color="currentColor" width={25} height={25} /></TrackingLoaderWrapper>
              {broadcastGpsCoordinates ? (
                <Text>{formatMessage(messages.tracking_sos_sending)}</Text>
              ) : (
                <Text>
                  {formatMessage(messages.tracking_sos_pending, { seconds: SOS_GPS_EMIT_DELAY - difference })}
                </Text>
              )}
            </Tracking>
          )}
        </>
      )}
     
      <RoutesMenu
        onClick={onSelectSubform}
        routes={routeMenuConfig}
        towingRecord={towingRecord}
        user={state.currentUser}
        canEditRoutes={editRoutes}
        useExtendedRoutes={useExtendedRoutes}
      />

      {useJobButtons && !lockedFor && (
        <Styling padding="0 2rem" paddingMobile="0 0.5rem">
          <ActionButtonGrid>
            <Button
              category="on"
              label={formatMessage(messages.end_job_button)}
              onClick={() => {
                onSelectSubform('end-towing-record', false)
              }}
              size="m"
              fontWeight="normal"
              maxWidth="100%"
              dataTestId="end-towing-record-button"
            />
            <Button
              category="on"
              label={formatMessage(messages.signature_button)}
              mobileLabel={formatMessage(messages.signature_button)}
              size="m"
              fontWeight="normal"
              maxWidth="100%"
              onClick={() => historyPush(`/towing-record/${towingRecord.id}/edit/payment`)}
            />
            <SendActionsDropdown
              showSendSms={showSendSms}
              onSendDocument={() => setShowSendForm(true)}
              onSendSms={() => setShowSendSmsDialog(true)}
            />
            {showSendSmsDialog && (
              <SendSmsDialog
                entityType="record"
                entityId={towingRecord.id}
                ordererPhone={towingRecord.orderer?.phone}
                onClose={() => setShowSendSmsDialog(false)}
              />
            )}
            <Button
              category="on"
              label={formatMessage(messages.take_photo_button)}
              size="m"
              fontWeight="normal"
              maxWidth="100%"
              onClick={() => {
                onSelectSubform('open-images', false)
              }}
            />
          </ActionButtonGrid>
        </Styling>
      )}
      {showSendForm && (() => {
        return (
          <SendDocumentForm
            towingRecordId={towingRecord.id}
            options={toSendDocumentOptions(towingRecord, formatMessage)}
            onClose={() => setShowSendForm(false)}
          />
        )
      })()}
      <SummaryBody
        towingRecord={towingRecord}
        onVehicleInfoLinkClick={onVehicleInfoLinkClick}
        vehicleInfoCheck={towingRecord.vehicleInfoCheck}
        refetchData={refetchData}
        imagesCount={imagesCount}
      />
    </>
  )
}

export const ViewTowingRecordContent = ViewTowingRecordContentIntl
